import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createMask } from '@ngneat/input-mask';
import { ProposalRequestForm } from 'src/app/classes/form-groups/proposal-request-form';
import { Company, Site, User } from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-quicksource-location-and-contact',
  templateUrl: './quicksource-location-and-contact.component.html',
  styleUrls: ['./quicksource-location-and-contact.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceLocationAndContactComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private CompanyService: CompanyService,
    private route: ActivatedRoute,
    private UtilityService: UtilityService
  ) {
    this.SharedDataService.listenForEvent<
      ProposalRequestForm | Site | User | boolean
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'SetProposalRequestForm' &&
          typeof data !== 'boolean' &&
          !(data instanceof User) &&
          !this.isSite(data)
        ) {
          this.ProposalRequestFormGroup = data;

          const {
            value: { contactEmail },
            controls: {
              Address: {
                value: { address1, address2, city, state, zip }
              }
            }
          } = this.ProposalRequestFormGroup!;

          // If new site is selected, need to repopulate the controls.

          if (!address1 && !address2 && !city && !state && !zip) {
            this.SharedDataService.sendEvent('GetSite');
          }

          if (contactEmail) {
            this.ContactEmails.push(...contactEmail.split(/[;,]/));
          }
          this._cdr.markForCheck();
        }

        if (eventName === 'SetSite') {
          if (
            typeof data !== 'boolean' &&
            !(data instanceof User) &&
            this.isSite(data)
          ) {
            this.ProposalRequestFormGroup!.controls['Address'].patchValue({
              address1: data?.StreetAddress?.address1 || undefined,
              address2: data.StreetAddress?.address2,
              state: data.StreetAddress?.state,
              city: data?.StreetAddress?.city || undefined,
              zip: data?.StreetAddress?.zip || undefined
            });
            this._cdr.markForCheck();
          } else {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error setting Site information.',
              { returnError: true }
            );
          }
        }

        if (eventName === 'SetDisableInput' && typeof data === 'boolean') {
          this.disableInput = data;
          this._cdr.markForCheck();
        }

        if (eventName === 'SetLoggedInUser' && data instanceof User) {
          this.LoggedInUser = data;

          if (this.ProposalRequestFormGroup && !this.isContactSet()) {
            this.setContact();
          }
          this._cdr.markForCheck();
        }
      }
    });

    this.SharedDataService.sendEvent('GetProposalRequestForm');
    this.SharedDataService.sendEvent('GetDisableInput');
    this.SharedDataService.sendEvent('GetLoggedInUser');

    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = paramMap.get('CompanyId');

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        this.CompanyId = Number(CompanyId);

        if (this.ProposalRequestFormGroup) {
          this.useClientAddressIfSiteMissingRequiredFields();
        }

        this.CompanyService.getCompany(this.CompanyId, {
          attributes: JSON.stringify(['id', 'country'])
        }).subscribe({
          next: (Company) => {
            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            this.Company = Company;

            const { country } = Company;

            if (!country) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing country information.',
                { returnError: true }
              );
            }

            this.LocalizationInfo =
              this.UtilityService.getLocalizedValuesMap(country);

            this.SubNationalDivisionService.getSubNationalDivisions({
              country
            }).subscribe({
              next: (SubNationalDivisions) => {
                this.SubNationalDivisions.push(...SubNationalDivisions);
                this._cdr.markForCheck();
              }
            });
            this._cdr.markForCheck();
          }
        });
      }
    });
  }

  isSite(data: ProposalRequestForm | Site | undefined): data is Site {
    return data instanceof Site;
  }

  removeCurrentSiteLocation() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request form.',
        { returnError: true }
      );
    }

    this.ProposalRequestFormGroup.controls['Address'].reset();
    this._cdr.markForCheck();
  }

  setCurrentSiteLocation() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request form.',
        { returnError: true }
      );
    }

    this.SharedDataService.sendEvent('GetSite');
  }

  useClientAddressIfSiteMissingRequiredFields() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request form.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    const {
      value: { address1, city, state, zip }
    } = this.ProposalRequestFormGroup.controls['Address'];

    // Check if any required field is missing
    const missingRequiredFields = !address1 || !city || !state || !zip;

    if (missingRequiredFields) {
      this.CompanyService.getCompany(this.CompanyId, {
        StreetAddress: JSON.stringify({})
      }).subscribe({
        next: (Company) => {
          if (!Company) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not load client information.',
              { returnError: true }
            );
          }

          // Use client's street address
          this.ProposalRequestFormGroup!.controls['Address'].patchValue({
            address1: Company.StreetAddress?.address1 || undefined,
            address2: Company.StreetAddress?.address2,
            city: Company.StreetAddress?.city || undefined,
            state: Company.StreetAddress?.state,
            zip: Company.StreetAddress?.zip || undefined
          });

          this._cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching client address:', err);
        }
      });
    }
  }

  isSiteSet() {
    const {
      value: { address1, address2, city, state, zip }
    } = this.ProposalRequestFormGroup!.controls['Address'];

    return address1 || address2 || city || state || zip;
  }

  removeContact() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request form.',
        { returnError: true }
      );
    }

    this.ProposalRequestFormGroup.patchValue({
      contactFirstName: undefined,
      contactLastName: undefined,
      contactEmail: undefined,
      contactPhone: undefined,
      contactPhoneExt: undefined,
      contactPhone2: undefined,
      contactPhone2Ext: undefined
    });
    this._cdr.markForCheck();
  }

  setContact() {
    if (!this.ProposalRequestFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request form.',
        { returnError: true }
      );
    }

    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing logged in user information.',
        { returnError: true }
      );
    }

    const {
      firstName,
      lastName,
      email,
      phone,
      extension,
      mobilePhone,
      mobilePhoneExtension
    } = this.LoggedInUser;

    if (!email) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Logged in user is missing email.',
        { returnError: true }
      );
    }

    this.ContactEmails.push(email);

    this.ProposalRequestFormGroup.patchValue({
      contactFirstName: firstName,
      contactLastName: lastName,
      contactEmail: email,
      contactPhone: phone || '',
      contactPhoneExt: extension,
      contactPhone2: mobilePhone,
      contactPhone2Ext: mobilePhoneExtension
    });
    this._cdr.markForCheck();
  }

  isContactSet() {
    const {
      value: {
        contactFirstName,
        contactLastName,
        contactEmail,
        contactPhone,
        contactPhone2
      }
    } = this.ProposalRequestFormGroup!;

    const hasNoContact = [
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhone,
      contactPhone2
    ].every((value) => !value);

    return !hasNoContact;
  }

  addEmail(emails: string) {
    this.ProposalRequestFormGroup!.patchValue({
      contactEmail: emails
    });
    this._cdr.markForCheck();
  }

  usZipMask = createMask('99999');
  canadianZipMask = createMask('A9A 9A9');
  phoneMask = createMask('(999) 999-9999');
  phoneExtensionMask = createMask({ regex: '\\d{1,6}', placeholder: '' });

  LoggedInUser!: User;
  CompanyId?: number;
  Company?: Company;
  ProposalRequestFormGroup?: ProposalRequestForm;
  SubNationalDivisions: SubNationalDivision[] = [];
  LocalizationInfo?: LocalizationInfo;
  disableInput: boolean = false;
  ContactEmails: string[] = [];

  ngOnInit(): void {}
}
